import React, { useEffect, useState } from "react";
import { Button, message } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import TaxDrawer from "./TaxesDrawer";
import API from "../../api";
import CustomTable from "../../components/Table/CustomTable";
import ConfirmationModal from "../../components/ConfirmationModal";
import { getUserRole } from "../../utils/auth";

const Taxes = () => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [taxes, setTaxes] = useState([]);
  const [selectedTax, setSelectedTax] = useState(null);
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    setUserRole(getUserRole());
  }, []);

  const getTaxes = async () => {
    const { current, pageSize } = paginationInfo;
    try {
      setLoading(true);
      const response = await API.get(
        `/taxes/paginate?page=${current}&limit=${pageSize}`
      );
      const { taxes: taxes, pagination } = response?.data || {};
      setTaxes(taxes || []);
      setPaginationInfo((prev) => ({
        ...prev,
        total: pagination?.total || 0,
      }));
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch taxes. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTaxes();
  }, [paginationInfo.current, paginationInfo.pageSize]);

  const handleDeleteClick = (tax) => {
    setTaxToDelete(tax);
    setIsDeleteModalVisible(true);
  };

  const handleDeleteTax = async () => {
    try {
      await API.delete(`/taxes/${taxToDelete?._id}`);
      message.success("Tax deleted successfully!");
      getTaxes();
    } catch (err) {
      console.error("Error deleting tax:", err);
      message.error("Failed to delete tax.");
    } finally {
      setIsDeleteModalVisible(false);
    }
  };

  const handleAddTax = async (newTax) => {
    try {
      await API.post("/taxes", newTax);
      message.success("Tax added successfully!");
      getTaxes();
    } catch (err) {
      console.error(err);
      message.error("Failed to add tax.");
    }
  };

  const handleEditTax = async (updatedTax) => {
    try {
      await API.put(`/taxes/${updatedTax?._id}`, updatedTax);
      message.success("Tax updated successfully!");
      getTaxes();
      setOpenDrawer(false);
    } catch (err) {
      console.error(err.response.data.message);
      message.error(err.response.data.message || "Failed to update tax.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        (paginationInfo.current - 1) * paginationInfo.pageSize + index + 1,
    },
    {
      title: "Tax Name",
      dataIndex: "taxName",
      key: "name",
    },
    {
      title: "Percentage",
      dataIndex: "percentage",
      key: "percentage",
    },
    userRole.editPermission == true && {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (record) => (
        <div>
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setIsEdit(true);
              setSelectedTax(record);
              setOpenDrawer(true);
            }}
          />
          {/* <Button
            danger
            icon={<DeleteOutlined />}
            style={{ marginLeft: 8 }}
            onClick={() => handleDeleteClick(record)}
          /> */}
        </div>
      ),
    },
  ];

  const handlePaginationChange = (current, pageSize) => {
    setPaginationInfo((prev) => ({
      ...prev,
      current: current,
      pageSize: pageSize,
    }));
  };

  return (
    <div
      style={{
        padding: "20px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setIsEdit(false);
            setSelectedTax(null);
            setOpenDrawer(true);
          }}
        >
          Add New Tax
        </Button>
      </div>

      <CustomTable
        columns={columns}
        dataSource={taxes} // Use filtered data here
        onPaginationChange={(pagination) =>
          setPaginationInfo({
            current: pagination.current,
            pageSize: pagination.pageSize,
          })
        }
        loading={loading}
        style={{ padding: "0 20px" }}
        total={paginationInfo.total}
      />

      <TaxDrawer
        open={openDrawer}
        setOpen={setOpenDrawer}
        data={selectedTax}
        isEdit={isEdit}
        onSubmit={isEdit ? handleEditTax : handleAddTax}
      />

      <ConfirmationModal
        visible={isDeleteModalVisible}
        onConfirm={handleDeleteTax}
        onCancel={() => setIsDeleteModalVisible(false)}
        title="Confirm Deletion"
        content="Are you sure you want to delete this tax? This action cannot be undone."
      />
    </div>
  );
};

export default Taxes;
